import { useState } from "react";
import type { FormEvent } from "react";
import { addAdmin, fetchAdmins, removeAdmin, ApiError } from "../api/client";
import { Alert } from "../components/Alert";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Spinner } from "../components/Spinner";
import { useAsyncData } from "../hooks/useAsyncData";
import { formatError } from "../utils/formatError";
import type { Admin } from "../api/types";

function formatDate(value: string): string {
  return new Date(value).toLocaleString();
}

export function AdminsScreen(): JSX.Element {
  const { data: admins, loading, error, reload } = useAsyncData(() => fetchAdmins(), []);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<Admin | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  const handleAdd = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setAdding(true);
    setAddError(null);
    try {
      await addAdmin({
        email: email.trim(),
        display_name: displayName.trim(),
      });
      setEmail("");
      setDisplayName("");
      reload();
    } catch (addErr) {
      setAddError(formatError(addErr));
    } finally {
      setAdding(false);
    }
  };

  const handleConfirmRemove = async (): Promise<void> => {
    if (!pendingRemoval) {
      return;
    }
    setRemoving(true);
    setRemoveError(null);
    try {
      await removeAdmin(pendingRemoval.user_key || pendingRemoval.email);
      setPendingRemoval(null);
      reload();
    } catch (removeErr) {
      // The 409 "refusing to remove the last admin" refusal is expected, not a crash: show its
      // message inline and leave the dialog open so the operator understands why.
      if (removeErr instanceof ApiError && removeErr.isConflict) {
        setRemoveError(removeErr.detail);
      } else {
        setRemoveError(formatError(removeErr));
      }
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return <Spinner label="Loading administrators…" />;
  }

  if (error) {
    return (
      <div className="admins-screen">
        <Alert variant="error">Could not load administrators: {formatError(error)}</Alert>
        <button type="button" className="button button--secondary" onClick={reload}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="admins-screen">
      <table className="data-table">
        <thead>
          <tr>
            <th scope="col">Email</th>
            <th scope="col">Display name</th>
            <th scope="col">User key</th>
            <th scope="col">Added by</th>
            <th scope="col">Added at</th>
            <th scope="col" aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {(admins ?? []).map((admin) => (
            <tr key={admin.user_key || admin.email}>
              <td>{admin.email || "—"}</td>
              <td>{admin.display_name || "—"}</td>
              <td>
                {/* Empty until the person's first sign-in binds their stable identity. */}
                {admin.user_key ? <code>{admin.user_key}</code> : <em>pending first sign-in</em>}
              </td>
              <td>{admin.added_by || "—"}</td>
              <td>{formatDate(admin.added_at)}</td>
              <td>
                <button
                  type="button"
                  className="button button--danger button--small"
                  onClick={() => {
                    setRemoveError(null);
                    setPendingRemoval(admin);
                  }}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
          {(admins ?? []).length === 0 && (
            <tr>
              <td colSpan={6}>No administrators are listed.</td>
            </tr>
          )}
        </tbody>
      </table>

      <section className="admins-screen__add">
        <h2>Add an administrator</h2>
        <form onSubmit={handleAdd}>
          <label htmlFor="admin-email">Email</label>
          <input
            id="admin-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="person@stjohnpartners.com"
            required
          />
          <label htmlFor="admin-display-name">Display name (optional)</label>
          <input
            id="admin-display-name"
            type="text"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
          {addError && <p className="field-error">{addError}</p>}
          <button
            type="submit"
            className="button button--primary"
            disabled={adding || !email.trim()}
          >
            {adding ? "Adding…" : "Add administrator"}
          </button>
        </form>
      </section>

      {pendingRemoval && (
        <ConfirmDialog
          title="Remove administrator"
          message={
            removeError ??
            `Remove ${pendingRemoval.display_name || pendingRemoval.email || pendingRemoval.user_key} as an administrator?`
          }
          confirmLabel="Remove"
          busy={removing}
          onConfirm={handleConfirmRemove}
          onCancel={() => {
            setPendingRemoval(null);
            setRemoveError(null);
          }}
        />
      )}
    </div>
  );
}
